import React, { useState } from 'react';
import {
    ActionIcon,
    Badge,
    Button,
    CloseButton,
    Group,
    Menu,
    Modal,
    Paper,
    Stack,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import {
    IconArrowRight,
    IconDeviceDesktop,
    IconDots,
    IconPencil,
    IconPlus,
    IconTrash,
} from '@tabler/icons-react';
import {
    DndContext,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    closestCenter,
    useSensor,
    useSensors,
    type DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    arrayMove,
    horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useSWR, { useSWRConfig } from 'swr';
import { notifications } from '@mantine/notifications';
import {
    deletePlaylist,
    listPlaylistDevices,
    updatePlaylist,
    updateDeviceV2Config,
    type Playlist,
    type PlaylistSceneEntry,
    type SceneTemplateId,
    type ShellComponent,
} from '../../../data/ShellConfig';
import type { Device } from '../../../data/Device';
import { useCsrf } from '../../../hooks/useCsrf';
import { track } from '../../../utils/mixpanel';
import { AnalyticsEvents } from '../../../analytics/events';
import SceneCard from './SceneCard';
import ScenePreviewFrame from './ScenePreviewFrame';
import PlaceholderCard from './PlaceholderCard';
import classes from './playlists.module.css';

type PlaylistCardProps = {
    playlist: Playlist;
    /** All org devices, for the "Add device" menu. */
    allDevices: Device[];
    /** Playlist id -> name, to name the current playlist when reassigning a device. */
    playlistNameById: Map<string, string>;
    /** Open the page-level Add-scene picker targeting this playlist. */
    onAddScene: (playlist: Playlist) => void;
    /** Open the page-level scene editor for a scene in this playlist. */
    onEditScene: (sceneId: string) => void;
    /** Org components, used to render each scene tile's live preview. */
    components: ShellComponent[];
};

const toScenesPayload = (entries: PlaylistSceneEntry[]) =>
    entries.map(entry => ({ sceneId: entry.sceneId, durationSeconds: entry.durationSeconds }));

const SortableSceneItem: React.FC<{
    entry: PlaylistSceneEntry;
    components: ShellComponent[];
    onDurationChange: (seconds: number) => void;
    onEdit: () => void;
    onRemove: () => void;
}> = ({ entry, components, onDurationChange, onEdit, onRemove }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: entry.id,
    });

    const scenePanels = entry.scene?.panels;
    const preview = scenePanels?.length ? (
        <ScenePreviewFrame
            variant="tile"
            templateId={(entry.scene?.templateId ?? 'full') as SceneTemplateId}
            panels={scenePanels.map(panel => ({
                componentId: panel.componentId,
                propOverrides: panel.propOverrides ?? [],
            }))}
            components={components}
        />
    ) : undefined;

    return (
        <div
            ref={setNodeRef}
            className={classes.stripItem}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                opacity: isDragging ? 0.5 : 1,
                cursor: 'grab',
                // Allow native scrolling of the strip when not dragging; only lock
                // it once a press-and-hold has actually started a drag.
                touchAction: isDragging ? 'none' : 'auto',
            }}
            {...attributes}
            {...listeners}
        >
            <SceneCard
                name={entry.scene?.name ?? 'Untitled scene'}
                durationSeconds={entry.durationSeconds}
                onDurationChange={onDurationChange}
                onEdit={onEdit}
                onDelete={onRemove}
                preview={preview}
            />
        </div>
    );
};

const PlaylistCard: React.FC<PlaylistCardProps> = ({
    playlist,
    allDevices,
    playlistNameById,
    onAddScene,
    onEditScene,
    components,
}) => {
    const { csrfToken } = useCsrf();
    const { mutate } = useSWRConfig();

    const devicesKey = `api/playlists/${playlist.id}/devices`;
    const { data: devices = [] } = useSWR(devicesKey, () => listPlaylistDevices(playlist.id));

    const [editingName, setEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState(playlist.name);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [sceneToRemove, setSceneToRemove] = useState<PlaylistSceneEntry | null>(null);
    // Set when the chosen device already belongs to another playlist; we confirm
    // the move before overwriting its assignment.
    const [deviceToReassign, setDeviceToReassign] = useState<Device | null>(null);

    const entries = playlist.playlistScenes ?? [];

    const saveScenes = (
        list: PlaylistSceneEntry[],
        name = playlist.name,
        action = 'edit'
    ) =>
        updatePlaylist(playlist.id, { name, scenes: toScenesPayload(list) }, csrfToken).then(() => {
            track(AnalyticsEvents.PlaylistUpdated, {
                source: 'playlist_editor',
                action,
                playlistId: playlist.id,
            });
            return mutate('api/playlists');
        });

    const handleRename = async () => {
        const name = nameDraft.trim();
        setEditingName(false);
        if (!name || name === playlist.name) {
            setNameDraft(playlist.name);
            return;
        }
        try {
            await saveScenes(entries, name, 'rename');
        } catch {
            setNameDraft(playlist.name);
            notifications.show({ color: 'red', message: 'Could not rename playlist.' });
        }
    };

    const handleDurationChange = async (entryId: string, durationSeconds: number) => {
        try {
            await saveScenes(
                entries.map(entry => (entry.id === entryId ? { ...entry, durationSeconds } : entry)),
                undefined,
                'duration'
            );
        } catch {
            notifications.show({ color: 'red', message: 'Could not update the duration.' });
        }
    };

    const requestRemoveScene = (entry: PlaylistSceneEntry) => {
        if (entries.length <= 1) {
            notifications.show({
                color: 'yellow',
                message: 'A playlist needs at least one scene — delete the playlist instead.',
            });
            return;
        }
        setSceneToRemove(entry);
    };

    const confirmRemoveScene = async () => {
        if (!sceneToRemove) return;
        try {
            await saveScenes(
                entries.filter(entry => entry.id !== sceneToRemove.id),
                undefined,
                'remove_scene'
            );
        } catch {
            notifications.show({ color: 'red', message: 'Could not remove the scene.' });
        }
        setSceneToRemove(null);
    };

    const handleDelete = async () => {
        try {
            await deletePlaylist(playlist.id, csrfToken);
            track(AnalyticsEvents.PlaylistDeleted, { playlistId: playlist.id });
            await mutate('api/playlists');
            notifications.show({ message: `Deleted “${playlist.name}”.` });
        } catch {
            notifications.show({ color: 'red', message: 'Could not delete the playlist.' });
        }
        setConfirmOpen(false);
    };

    // Reassigning a device also changes the source playlist's device list, so
    // revalidate every playlist's devices — not just this card's.
    const revalidateDevices = () =>
        Promise.all([
            mutate(
                key =>
                    typeof key === 'string' &&
                    key.startsWith('api/playlists/') &&
                    key.endsWith('/devices')
            ),
            mutate('api/playlists'),
            mutate('api/devices'),
        ]);

    const assignDevice = async (deviceId: string) => {
        try {
            await updateDeviceV2Config(deviceId, playlist.id, csrfToken);
            track(AnalyticsEvents.PlaylistUpdated, {
                source: 'device',
                action: 'assign',
                via: 'playlist_card',
                playlistId: playlist.id,
            });
            await revalidateDevices();
        } catch {
            notifications.show({ color: 'red', message: 'Could not attach the device.' });
        }
    };

    const handleAddDevice = (device: Device) => {
        // A device has exactly one playlist. If it's already on a different one,
        // confirm the move before overwriting; otherwise assign straight away.
        if (device.playlistId && device.playlistId !== playlist.id) {
            setDeviceToReassign(device);
            return;
        }
        void assignDevice(device.id);
    };

    const confirmReassign = async () => {
        if (!deviceToReassign) return;
        await assignDevice(deviceToReassign.id);
        setDeviceToReassign(null);
    };

    const handleRemoveDevice = async (deviceId: string) => {
        try {
            await updateDeviceV2Config(deviceId, null, csrfToken);
            track(AnalyticsEvents.PlaylistUpdated, {
                source: 'device',
                action: 'remove',
                via: 'playlist_card',
                playlistId: playlist.id,
            });
            await revalidateDevices();
        } catch {
            notifications.show({ color: 'red', message: 'Could not remove the device.' });
        }
    };

    // A small drag threshold so tapping delete or editing the duration inside a
    // tile doesn't start a reorder.
    const sensors = useSensors(
        // Desktop: start dragging after a small movement.
        useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
        // Touch: press-and-hold ~250ms to start a drag, so a quick swipe scrolls
        // through the scenes instead of reordering them. Moving more than 8px
        // before the hold completes cancels it and lets the strip scroll.
        useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = entries.findIndex(entry => entry.id === active.id);
        const newIndex = entries.findIndex(entry => entry.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return;
        saveScenes(arrayMove(entries, oldIndex, newIndex), undefined, 'reorder').catch(() =>
            notifications.show({ color: 'red', message: 'Could not reorder the scenes.' })
        );
    };

    const attachedIds = new Set(devices.map(device => device.id));
    const availableDevices = allDevices.filter(device => !attachedIds.has(device.id));

    return (
        <Paper withBorder radius="md" p="lg">
            <Group justify="space-between" wrap="nowrap" align="flex-start" mb="md">
                <Stack gap="xs" style={{ minWidth: 0 }}>
                    <Group gap="xs" wrap="nowrap">
                        {editingName ? (
                            <TextInput
                                value={nameDraft}
                                onChange={event => setNameDraft(event.currentTarget.value)}
                                onBlur={handleRename}
                                onKeyDown={event => {
                                    if (event.key === 'Enter') handleRename();
                                    if (event.key === 'Escape') {
                                        setEditingName(false);
                                        setNameDraft(playlist.name);
                                    }
                                }}
                                autoFocus
                                size="sm"
                                aria-label="Playlist name"
                            />
                        ) : (
                            <>
                                <Title order={4} style={{ overflowWrap: 'anywhere' }}>
                                    {playlist.name}
                                </Title>
                                <ActionIcon
                                    variant="subtle"
                                    color="gray"
                                    aria-label="Rename playlist"
                                    onClick={() => {
                                        setNameDraft(playlist.name);
                                        setEditingName(true);
                                    }}
                                >
                                    <IconPencil size={16} />
                                </ActionIcon>
                            </>
                        )}
                    </Group>

                    <Group gap="xs" wrap="wrap">
                        {devices.map(device => (
                            <Badge
                                key={device.id}
                                variant="light"
                                color="gray"
                                size="lg"
                                tt="none"
                                leftSection={<IconDeviceDesktop size={12} />}
                                rightSection={
                                    <CloseButton
                                        size="xs"
                                        aria-label={`Remove ${device.name}`}
                                        onClick={() => handleRemoveDevice(device.id)}
                                    />
                                }
                            >
                                {device.name}
                            </Badge>
                        ))}
                        <Menu position="bottom-start" withinPortal>
                            <Menu.Target>
                                <Button
                                    variant="default"
                                    size="compact-xs"
                                    leftSection={<IconPlus size={12} />}
                                >
                                    Add device
                                </Button>
                            </Menu.Target>
                            <Menu.Dropdown>
                                {availableDevices.length === 0 ? (
                                    <Menu.Item disabled>No devices available</Menu.Item>
                                ) : (
                                    availableDevices.map(device => (
                                        <Menu.Item
                                            key={device.id}
                                            onClick={() => handleAddDevice(device)}
                                        >
                                            {device.name}
                                        </Menu.Item>
                                    ))
                                )}
                            </Menu.Dropdown>
                        </Menu>
                    </Group>
                </Stack>

                <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" aria-label="Playlist actions">
                            <IconDots size={18} />
                        </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item
                            color="red"
                            leftSection={<IconTrash size={14} />}
                            onClick={() => setConfirmOpen(true)}
                        >
                            Delete playlist
                        </Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            </Group>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={entries.map(entry => entry.id)}
                    strategy={horizontalListSortingStrategy}
                >
                    <div className={classes.strip}>
                        {entries.map(entry => (
                            <SortableSceneItem
                                key={entry.id}
                                entry={entry}
                                components={components}
                                onDurationChange={seconds => handleDurationChange(entry.id, seconds)}
                                onEdit={() => onEditScene(entry.sceneId)}
                                onRemove={() => requestRemoveScene(entry)}
                            />
                        ))}
                        {/* Not sortable — always the trailing tile. */}
                        <div className={classes.stripItem}>
                            <PlaceholderCard label="Add scene" onClick={() => onAddScene(playlist)} />
                        </div>
                    </div>
                </SortableContext>
            </DndContext>

            <Modal
                opened={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                title="Delete playlist"
                centered
            >
                <Text size="sm" mb="lg">
                    Delete “{playlist.name}”? Devices playing it will stop until reassigned.
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setConfirmOpen(false)}>
                        Cancel
                    </Button>
                    <Button color="red" onClick={handleDelete}>
                        Delete
                    </Button>
                </Group>
            </Modal>

            <Modal
                opened={deviceToReassign !== null}
                onClose={() => setDeviceToReassign(null)}
                title="Move device to this playlist?"
                centered
            >
                <Group gap="sm" wrap="nowrap" align="center" mb="md">
                    <Badge variant="light" color="gray" size="lg" tt="none">
                        {(deviceToReassign?.playlistId &&
                            playlistNameById.get(deviceToReassign.playlistId)) ??
                            'Another playlist'}
                    </Badge>
                    <IconArrowRight
                        size={18}
                        color="var(--mantine-color-dimmed)"
                        style={{ flexShrink: 0 }}
                    />
                    <Badge variant="light" size="lg" tt="none">
                        {playlist.name}
                    </Badge>
                </Group>
                <Text size="sm" mb="lg">
                    “{deviceToReassign?.name}” is currently playing “
                    {(deviceToReassign?.playlistId &&
                        playlistNameById.get(deviceToReassign.playlistId)) ??
                        'another playlist'}
                    ”. A device can only run one playlist, so moving it to “{playlist.name}” will stop
                    the current one on this device.
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setDeviceToReassign(null)}>
                        Cancel
                    </Button>
                    <Button onClick={confirmReassign}>Move device</Button>
                </Group>
            </Modal>

            <Modal
                opened={sceneToRemove !== null}
                onClose={() => setSceneToRemove(null)}
                title="Remove scene"
                centered
            >
                <Text size="sm" mb="lg">
                    Remove “{sceneToRemove?.scene?.name ?? 'this scene'}” from “{playlist.name}”? The
                    scene itself stays in your library.
                </Text>
                <Group justify="flex-end">
                    <Button variant="default" onClick={() => setSceneToRemove(null)}>
                        Cancel
                    </Button>
                    <Button color="red" onClick={confirmRemoveScene}>
                        Remove
                    </Button>
                </Group>
            </Modal>
        </Paper>
    );
};

export default PlaylistCard;
